import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import cx from 'classnames'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { withStyles } from '@material-ui/core/styles'
import { Translate } from 'react-localize-redux'
import {
  IconButton,
  Fade,
  Menu,
  MenuItem,
  Typography,
  Button,
  Input,
  FormControlLabel,
  Radio
} from '@material-ui/core'

import { ArrowIcon } from 'icons'
import { useAlertForm } from '../../AlertFormProvider'
import { LEVEL_LABEL_KEYS, YELLOW_LEVEL, RED_LEVEL } from '../../alertLevelLimits'
import styles from '../../ParameterAlertForm.module.scss'
import stepStyles from './Values.module.scss'
import * as DataUtils from 'utils/data'
import DataTypes from 'constants/DataTypes'
import MeasurementUnits from 'constants/MeasurementUnits'

// Keep this outside Values so it is not rebuilt on every render.
const StyledMenu = withStyles({
  paper: {
    border: '1px solid #dddddd',
    'border-radius': '13px',
    padding: '0',
    transition: 'color .3s ease',
    cursor: 'pointer',
    color: '#333333',
    width: '200px',
    borderRadius: '13px'
  }
})(props => <Menu {...props} />)

// Check whether an alert value is valid.
const checkTypeRange = (value, type) => {
  if (type === DataTypes.CO2) return value > 400
  return value > 0
}

const getRangeErrorText = (dataType, translate) => {
  if (dataType === DataTypes.CO2) return translate('addAlertModal.rangeCo2Error')
  return translate('addAlertModal.rangeError')
}

// Get the help page for a parameter.
const getTypeInfoLink = type => {
  const links = {
    [DataTypes.TEMPERATURE]: 'https://www.inbiotair.com/wikinbiot/temperatura/',
    [DataTypes.HUMIDITY]: 'https://www.inbiotair.com/wikinbiot/humedad/',
    [DataTypes.CO2]: 'https://www.inbiotair.com/wikinbiot/co2/',
    [DataTypes.VOCS]: 'https://www.inbiotair.com/wikinbiot/covs/',
    [DataTypes.FORMALDEHYDE]: 'https://www.inbiotair.com/wikinbiot/formaldehido/',
    [DataTypes.PM25]: 'https://www.inbiotair.com/wikinbiot/particulasensuspe/',
    [DataTypes.PM10]: 'https://www.inbiotair.com/wikinbiot/particulasensuspe/',
    [DataTypes.PM1]: 'https://www.inbiotair.com/wikinbiot/particulasensuspe/',
    [DataTypes.PM4]: 'https://www.inbiotair.com/wikinbiot/particulasensuspe/',
    [DataTypes.NOISE]: 'https://www.inbiotair.com/wikinbiot/noise',
    [DataTypes.COVID_19]: 'https://www.inbiotair.com/wikinbiot/indicador-resistencia-virus',
    [DataTypes.IAQ]: 'https://www.inbiotair.com/wikinbiot/indicador-calidad-de-aire-interior',
    [DataTypes.VENTILATION_INDICATOR]:
      'https://www.inbiotair.com/wikinbiot/indicador-eficacia-ventilacion',
    [DataTypes.MOLD_INDICATOR]:
      'https://www.inbiotair.com/wikinbiot/resistance-to-mold-growth-indicator',
    [DataTypes.THERMAL_INDICATOR]:
      'https://www.inbiotair.com/wikinbiot/indicador-confort-termohigrometrico'
  }
  return links[type] || 'https://www.inbiotair.com/wikinbiot'
}

// Show the help text that matches the selected unit.
const ParamInfoText = ({ dataType, measurementUnits }) => {
  if (dataType === 'tempHigh' || dataType === 'tempLow') {
    if (measurementUnits[DataTypes.TEMPERATURE] === MeasurementUnits.FAHRENHEIT)
      return (
        <Translate
          id={dataType + 'InfoNFahrenheit'}
          options={{ renderInnerHtml: true }}
        />
      )
    if (measurementUnits[DataTypes.TEMPERATURE] === MeasurementUnits.KELVIN)
      return (
        <Translate
          id={dataType + 'InfoNKelvin'}
          options={{ renderInnerHtml: true }}
        />
      )
  }
  if (
    dataType === 'formaldehyde' &&
    measurementUnits[DataTypes.FORMALDEHYDE] === MeasurementUnits.FORMALDEHYDE_PPB
  )
    return (
      <Translate
        id={dataType + 'InfoNFormaldehyde'}
        options={{ renderInnerHtml: true }}
      />
    )
  if (dataType === 'vocs') {
    if (measurementUnits[DataTypes.VOCS] === MeasurementUnits.VOCS_UG_WELL)
      return (
        <Translate
          id={dataType + 'InfoNVocsWell'}
          options={{ renderInnerHtml: true }}
        />
      )
    if (measurementUnits[DataTypes.VOCS] === MeasurementUnits.VOCS_UG_RESET)
      return (
        <Translate
          id={dataType + 'vocsInfoNVocsReset'}
          options={{ renderInnerHtml: true }}
        />
      )
  }
  if (dataType === 'o3' && measurementUnits[DataTypes.O3] === MeasurementUnits.O3_UG)
    return (
      <Translate
        id={dataType + 'InfoNO3'}
        options={{ renderInnerHtml: true }}
      />
    )
  if (dataType === 'co' && measurementUnits[DataTypes.CO] === MeasurementUnits.CO_MG)
    return (
      <Translate
        id={dataType + 'InfoNCO'}
        options={{ renderInnerHtml: true }}
      />
    )

  return (
    <Translate
      id={dataType + 'InfoN'}
      options={{ renderInnerHtml: true }}
    />
  )
}

// The user chooses a preset alert level or enters a value.
const Values = () => {
  const {
    state: { typeSelected, valuesData, levelType, level },
    actions,
    meta: { translate, measurementUnits }
  } = useAlertForm()

  const { register, errors, setError, clearErrors } = useForm()

  const [anchorEl, setAnchorEl] = useState(null)
  const [infoTab, setInfoTab] = useState('')
  const [openInfo, setOpenInfo] = useState({})

  const infoTabRef = useRef(null)
  const buttonRefIcon = useRef(null)

  // Close the help box when the user clicks outside it.
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        infoTabRef.current &&
        !infoTabRef.current.contains(event.target) &&
        buttonRefIcon.current &&
        !buttonRefIcon.current.contains(event.target)
      )
        setInfoTab('')
    }
    if (openInfo) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openInfo])

  const levelsOptions = [YELLOW_LEVEL, RED_LEVEL]
  const levelLabel = level ? translate(LEVEL_LABEL_KEYS[level]) : translate('selectLevel')

  const unityMeasure = {
    iaq: DataUtils.getDataUnit(DataTypes.IAQ, measurementUnits[DataTypes.IAQ]),
    covid19: DataUtils.getDataUnit(DataTypes.COVID_19, measurementUnits[DataTypes.COVID_19]),
    tempLow: DataUtils.getDataUnit(DataTypes.TEMPERATURE, measurementUnits[DataTypes.TEMPERATURE]),
    tempHigh: DataUtils.getDataUnit(DataTypes.TEMPERATURE, measurementUnits[DataTypes.TEMPERATURE]),
    humLow: DataUtils.getDataUnit(DataTypes.HUMIDITY, measurementUnits[DataTypes.HUMIDITY]),
    humHigh: DataUtils.getDataUnit(DataTypes.HUMIDITY, measurementUnits[DataTypes.HUMIDITY]),
    co2: DataUtils.getDataUnit(DataTypes.CO2, measurementUnits[DataTypes.CO2]),
    radon: DataUtils.getDataUnit(DataTypes.RADON, measurementUnits[DataTypes.RADON]),
    formaldehyde: DataUtils.getDataUnit(
      DataTypes.FORMALDEHYDE,
      measurementUnits[DataTypes.FORMALDEHYDE]
    ),
    vocs: DataUtils.getDataUnit(DataTypes.VOCS, measurementUnits[DataTypes.VOCS]),
    vocsIndex: DataUtils.getDataUnit(DataTypes.VOCS_INDEX, measurementUnits[DataTypes.VOCS_INDEX]),
    pm10: DataUtils.getDataUnit(DataTypes.PM10, measurementUnits[DataTypes.PM10]),
    pm25: DataUtils.getDataUnit(DataTypes.PM25, measurementUnits[DataTypes.PM25]),
    pm1: DataUtils.getDataUnit(DataTypes.PM1, measurementUnits[DataTypes.PM1]),
    pm4: DataUtils.getDataUnit(DataTypes.PM4, measurementUnits[DataTypes.PM4]),
    nox: DataUtils.getDataUnit(DataTypes.NOX, measurementUnits[DataTypes.NOX]),
    no2: DataUtils.getDataUnit(DataTypes.NO2, measurementUnits[DataTypes.NO2]),
    o3: DataUtils.getDataUnit(DataTypes.O3, measurementUnits[DataTypes.O3]),
    co: DataUtils.getDataUnit(DataTypes.CO, measurementUnits[DataTypes.CO]),
    [DataTypes.NOISE]: DataUtils.getDataUnit(DataTypes.NOISE, measurementUnits[DataTypes.NOISE]),
    [DataTypes.LIGHT]: DataUtils.getDataUnit(DataTypes.LIGHT, measurementUnits[DataTypes.LIGHT])
  }

  const handleChangeLevelTypes = event => {
    actions.chooseLevelType(event.target.value)
  }

  const handleLevel = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleOptionClick = option => {
    clearErrors('typesInput')
    actions.chooseLevel(option)
    setAnchorEl(null)
  }

  const handleInputChange = (event, dataType) => {
    actions.setAlertValue(dataType, event.target.value)
    if (!checkTypeRange(event.target.value, dataType)) {
      setError('typesInput.' + dataType, { shouldFocus: 'typesInput' })
    } else {
      clearErrors('typesInput.' + dataType)
    }
  }

  const handleInfo = keyInfo => {
    setOpenInfo(prev => ({ ...prev, [keyInfo]: !prev[keyInfo] }))
    setInfoTab(prev => (prev === keyInfo ? '' : keyInfo))
  }

  const checkTypeError = dataType => errors.typesInput?.[dataType]

  const handleNext = () => {
    const firstErrorType = typeSelected.find(
      dataType => !valuesData[dataType] || !checkTypeRange(valuesData[dataType], dataType)
    )

    if (firstErrorType) {
      setError('typesInput.' + firstErrorType, { shouldFocus: 'typesInput' })
      return
    }

    actions.completeValues()
  }

  return (
    <div>
      <div className={styles.nav}>
        <Typography className={styles.subheaderParams}>
          {translate('addParameterAlertModal.navParams')}
        </Typography>
        <Typography className={styles.subheaderValuesSelected}>
          {translate('addParameterAlertModal.navValues')}
        </Typography>
        <Typography className={styles.subheaderDevices}>
          {translate('addParameterAlertModal.navDevices')}
        </Typography>
        <Typography className={styles.subheaderParams}>
          {translate('addParameterAlertModal.navEmails')}
        </Typography>
      </div>

      <div className={stepStyles.spaceBetween}>
        <div className={styles.subInfo}>
          <Typography>{translate('alertValue')}</Typography>
        </div>

        {/* Choose a preset alert level. */}
        <div className={stepStyles.wrapperDefaultValues}>
          <div className={stepStyles.radioFormDiv}>
            <FormControlLabel
              className={styles.checkboxForm}
              control={
                <Radio
                  className={cx(styles.checkbox)}
                  onChange={handleChangeLevelTypes}
                  checked={levelType === 'Predetermined'}
                  value='Predetermined'
                  id='Predetermined'
                  classes={{ checked: styles.checkedBox }}
                  required
                  name='Predetermined'
                  inputRef={register()}
                  disableRipple
                />
              }
              label={translate('predetermined')}
            />
          </div>
          <div className={stepStyles.values}>
            <Button
              disabled={levelType === 'Personalized'}
              disableRipple
              aria-controls='fade-menu'
              aria-haspopup='true'
              onClick={handleLevel}
              className={stepStyles.navItem}
            >
              {levelLabel}
              <KeyboardArrowDownIcon />
            </Button>
            <StyledMenu
              elevation={0}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              id='fade-menu'
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              TransitionComponent={Fade}
            >
              {levelsOptions.map(option => (
                <MenuItem
                  key={option}
                  onClick={() => handleOptionClick(option)}
                  className={cx(
                    { [stepStyles.menuItem]: option !== level },
                    { [stepStyles.menuItemFocus]: option === level }
                  )}
                >
                  {translate(LEVEL_LABEL_KEYS[option])}
                </MenuItem>
              ))}
            </StyledMenu>
          </div>
        </div>

        <FormControlLabel
          className={styles.checkboxForm}
          control={
            <Radio
              checked={levelType === 'Personalized'}
              onChange={handleChangeLevelTypes}
              value='Personalized'
              className={cx(styles.checkbox)}
              id='Personalized'
              classes={{ checked: styles.checkedBox }}
              required
              name='Personalized'
              inputRef={register()}
              disableRipple
            />
          }
          label={translate('Personalized')}
        />

        {/* Show one input for each selected parameter. */}
        <div className={stepStyles.paramWrapper}>
          {typeSelected.map((dataType, i) => (
            <div
              className={stepStyles.wrap}
              key={dataType}
            >
              <div className={stepStyles.wrapperInfo}>
                <div
                  className={
                    levelType === 'Personalized' ? styles.dataInputEnable : styles.dataInput
                  }
                >
                  {translate(dataType)}
                  <div className={styles.wrapperInfoButton}>
                    <IconButton
                      ref={buttonRefIcon}
                      className={styles.infoButton}
                      variant='outlined'
                      onClick={() => handleInfo(dataType)}
                      name='infoParam'
                      disableRipple
                    >
                      <InfoOutlinedIcon />
                    </IconButton>
                  </div>
                </div>
                <div className={styles.wrapperDataInfo}>
                  <div className={stepStyles.inputField}>
                    <Input
                      classes={{ checked: styles.checkedBox, input: stepStyles.inputUnit }}
                      disabled={levelType === 'Predetermined'}
                      onChange={event => handleInputChange(event, dataType)}
                      required
                      error={checkTypeError(dataType)}
                      position='end'
                      type='number'
                      name={`typesInput[${dataType}]`}
                      value={valuesData[dataType] ?? ''}
                      inputRef={register()}
                      disableRipple
                    />
                  </div>
                  <div className={stepStyles.inputUnity}>{unityMeasure[dataType]}</div>
                </div>
              </div>

              {infoTab === dataType && (
                <div
                  ref={infoTabRef}
                  className={stepStyles.caja}
                >
                  <ParamInfoText
                    dataType={dataType}
                    measurementUnits={measurementUnits}
                  />
                  <Button
                    fullWidth
                    disableRipple
                    variant='contained'
                    className={stepStyles.buttonSiguiente}
                    onClick={e => {
                      e.preventDefault()
                      window.open(getTypeInfoLink(dataType), '_blank')
                    }}
                  >
                    {translate('infoModal.moreInfo')}
                  </Button>
                </div>
              )}

              <div className={styles.wrapperError}>
                {errors.typesInput?.[dataType] && (
                  <div className={styles.error}>
                    <small>{getRangeErrorText(dataType, translate)}</small>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.buttonWrapper}>
        <Button
          fullWidth
          disableRipple
          variant='contained'
          className={styles.buttonSiguiente1}
          onClick={handleNext}
        >
          {translate('next')}
          <ArrowIcon className={styles.arrow} />
        </Button>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          fullWidth
          variant='contained'
          className={styles.buttonCancelar}
          onClick={actions.goBack}
        >
          <ArrowIcon
            className={styles.arrow}
            direction='left'
          />
          {translate('back')}
        </Button>
      </div>
    </div>
  )
}

export default Values
